import React, { useState, useEffect } from 'react';
import { Tabs, Table, Tag, Card, Button, message, Space, Empty, Pagination } from 'antd';
import { getDirectusClient } from '../../lib/directus';
import { readItems, updateItem } from '@directus/sdk';
import Layouts from '../../components/Layouts';
import { useSelector } from 'react-redux';

import LiquidacionDetailsModal from './components/LiquidacionDetailsModal';
import SearchFilter from './components/SearchFilter';
import TotalsSummary from './components/TotalsSummary';

const { TabPane } = Tabs;

export default function LiquidacionList() {
  const user = useSelector((state) => state.userdata);
  const [loading, setLoading] = useState(false);
  const [liquidaciones, setLiquidaciones] = useState({
    local: [],
    tropas: [],
    rutas: []
  });
  
  const [selectedLiquidacion, setSelectedLiquidacion] = useState(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedCajas, setSelectedCajas] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [filteredLiquidaciones, setFilteredLiquidaciones] = useState({
    local: [],
    tropas: [],
    rutas: []
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 25,
    total: 0,
  });

  // Add new state for selected rows
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  // Add new state
  const [aceptadaFilter, setAceptadaFilter] = useState(false);

  const columns = [
    {
      title: 'Responsable',
      dataIndex: ['responsable', 'first_name'],
      key: 'responsable',
      render: (text, record) => `${record.responsable?.first_name || ''} ${record.responsable?.last_name || ''}`
    },
    {
      title: 'Cajas',
      dataIndex: 'cajas',
      key: 'cajas',
      render: (cajas) => cajas?.length || 0
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado) => {
        let color = 'blue';
        if (estado === 'incompleta') color = 'orange';
        if (estado === 'anulada') color = 'red';
        if (estado === 'creada') color = 'green';
        
        return (
          <Tag color={color}>
            {estado?.toUpperCase()}
          </Tag>
        );
      }
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            onClick={() => handleViewDetails(record)}
          >
            Ver Detalles
          </Button>
          <Button
            type="primary"
            onClick={() => handleAceptarLiquidacion(record.id)}
            disabled={record.aceptada || record.estado === 'anulada'}
          >
            Aceptar Liquidación
          </Button>
        </Space>
      )
    }
  ];

  // Add row selection config
  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => {
      setSelectedRowKeys(selectedKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: record.aceptada || record.estado === 'anulada',
    }),
  };

  const handleAceptarLiquidacion = async (liquidacionId) => {
    try {
      const directus = await getDirectusClient();
      await directus.request(
        updateItem('liquidacion', liquidacionId, {
          aceptada: true,
          aceptada_por: user.id,
          fecha_aceptacion: new Date().toISOString()
        })
      );
      message.success('Liquidación aceptada correctamente');
      fetchLiquidaciones();
    } catch (error) {
      console.error('Error accepting liquidacion:', error);
      message.error('Error al aceptar la liquidación');
    }
  };

  // Add function to handle bulk accept
  const handleBulkAccept = async () => {
    try {
      const directus = await getDirectusClient();
      
      // Show loading message
      const loadingMessage = message.loading('Aceptando liquidaciones...', 0);
      
      // Process all selected liquidaciones
      const promises = selectedRowKeys.map(id => 
        directus.request(
          updateItem('liquidacion', id, {
            aceptada: true,
            aceptada_por: user.id,
            fecha_aceptacion: new Date().toISOString()
          })
        )
      );

      await Promise.all(promises);
      
      // Clear selection and refresh data
      setSelectedRowKeys([]);
      fetchLiquidaciones();
      
      // Show success message
      loadingMessage();
      message.success(`${selectedRowKeys.length} liquidaciones aceptadas correctamente`);
    } catch (error) {
      console.error('Error accepting liquidaciones:', error);
      message.error('Error al aceptar las liquidaciones');
    }
  };

  // Update fetchLiquidaciones function
  const fetchLiquidaciones = async (page = 1, pageSize = 25, search = '', date = null, aceptada = false) => {
    setLoading(true);
    try {
      const directus = await getDirectusClient();
      
      const filter = {
        _and: [
          { aceptada: { _eq: aceptada } }
        ]
      };

      if (date && date[0] && date[1]) {
        filter._and.push({
          date_created: {
            _between: [
              date[0].toISOString(),
              date[1].toISOString()
            ]
          }
        });
      }

      // Get total count with filters
      const countResponse = await directus.request(
        readItems('liquidacion', {
          aggregate: {
            count: 'id'
          },
          filter: filter
        })
      );

      const totalItems = countResponse[0]?.count?.id || 0;

      // Get paginated data with date filter
      const response = await directus.request(
        readItems('liquidacion', {
          fields: [
            'id',
            'responsable.first_name',
            'responsable.last_name',
            'cajas',
            'estado',
            'tipo',
            'aceptada',
            'aceptada_por.first_name',
            'fecha_aceptacion',
            'total_facturas',
            'total_credito',
            'diferencia',
            'total_devoluciones',
            'novedades',
            'date_created'
          ],
          filter: filter,
          sort: ['-date_created'],
          limit: pageSize,
          offset: (page - 1) * pageSize
        })
      );

      const grouped = response.reduce((acc, liquidacion) => {
        const tipo = liquidacion.tipo || 'local';
        acc[tipo] = acc[tipo] || [];
        acc[tipo].push(liquidacion);
        return acc;
      }, { local: [], tropas: [], rutas: [] });

      setLiquidaciones(grouped);
      setPagination({
        ...pagination,
        current: page,
        total: totalItems
      });
    } catch (error) {
      console.error('Error fetching liquidaciones:', error);
      message.error('Error al cargar las liquidaciones');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (liquidacion) => {
    setSelectedLiquidacion(liquidacion);
    setDetailsVisible(true);
    
    try {
      const directus = await getDirectusClient();
      const cajas = await directus.request(
        readItems('cajas', {
          fields: [
            'id',
            'vendedor.first_name',
            'vendedor.last_name',
            'estado',
            'liquidada',
            'fecha_apertura',
            'fecha_cierre'
          ],
          filter: {
            id: {
              _in: liquidacion.cajas
            }
          }
        })
      );
      setSelectedCajas(cajas);
    } catch (error) {
      console.error('Error fetching cajas details:', error);
      message.error('Error al cargar los detalles de las cajas');
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination({ ...pagination, current: 1 });
    fetchLiquidaciones(1, pagination.pageSize, value, dateRange, aceptadaFilter);
  };

  const handleDateChange = (dates) => {
    setDateRange(dates);
    setPagination({ ...pagination, current: 1 });
    fetchLiquidaciones(1, pagination.pageSize, searchText, dates, aceptadaFilter);
  };

  // Add handler for aceptada filter change
  const handleAceptadaChange = (value) => {
    setAceptadaFilter(value);
    setPagination({ ...pagination, current: 1 });
    fetchLiquidaciones(1, pagination.pageSize, searchText, dateRange, value);
  };

  // Modify renderTabContent to include bulk accept button and row selection
  // Update renderTabContent to include the new filter
  const renderTabContent = (tipo) => (
    <>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <SearchFilter
          onDateChange={handleDateChange}
          onAceptadaChange={handleAceptadaChange}
          loading={loading}
        />
        {selectedRowKeys.length > 0 && (
          <Button
            type="primary"
            onClick={handleBulkAccept}
            loading={loading}
          >
            Aceptar {selectedRowKeys.length} Liquidación(es)
          </Button>
        )}
      </Space>
      
      {/* Add TotalsSummary component */}
      <TotalsSummary liquidaciones={liquidaciones[tipo]} />
      
      <Table
        columns={columns}
        dataSource={liquidaciones[tipo]}
        loading={loading}
        rowKey="id"
        rowSelection={rowSelection}
        pagination={false}
        locale={{
          emptyText: <Empty 
            description={`No hay liquidaciones de ${tipo}`} 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        }}
      />
      <Pagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={pagination.total}
        showSizeChanger={true}
        showQuickJumper={true}
        showTotal={(total) => `Total: ${total} registros`}
        pageSizeOptions={['10', '25', '50', '100']}
        onChange={(page, pageSize) => {
          setPagination({
            ...pagination,
            current: page,
            pageSize: pageSize,
          });
          fetchLiquidaciones(page, pageSize, searchText, dateRange, aceptadaFilter);
        }}
        style={{ marginTop: 16, textAlign: 'right' }}
      />
    </>
  );

  // Update initial useEffect to pass default aceptada value
  useEffect(() => {
    fetchLiquidaciones(1, pagination.pageSize, '', null, false);
  }, []);

  useEffect(() => {
    setFilteredLiquidaciones(liquidaciones);
  }, [liquidaciones]);

  return (
    <Layouts>
      <Card>
        <Tabs defaultActiveKey="local">
          <TabPane tab="Local" key="local">
            {renderTabContent('local')}
          </TabPane>
          <TabPane tab="Tropas" key="tropas">
            {renderTabContent('tropas')}
          </TabPane>
          <TabPane tab="Rutas" key="rutas">
            {renderTabContent('rutas')}
          </TabPane>
        </Tabs>
      </Card>
      
      <LiquidacionDetailsModal
        visible={detailsVisible}
        liquidacion={selectedLiquidacion}
        cajas={selectedCajas}
        onClose={() => {
          setDetailsVisible(false);
          setSelectedLiquidacion(null);
          setSelectedCajas([]);
        }}
      />
    </Layouts>
  );
}
